import { createServerStateStore } from "./serverState";

export type SlaughterOpStatus =
  | "queued"
  | "entered"
  | "cut"
  | "packing"
  | "done";

export interface SlaughterOpRecord {
  animalId: number;
  status: SlaughterOpStatus;
  packingZone?: number | null;
  [key: string]: any;
}

function normalizeStatus(value?: string): SlaughterOpStatus {
  const raw = String(value ?? "").toLowerCase();

  if (raw === "entered") return "entered";
  if (raw === "cut") return "cut";
  if (raw === "packing") return "packing";
  if (raw === "done") return "done";

  return "queued";
}

function normalizePackingZone(value: unknown): number | null {
  const parsed = Number(value ?? 0);
  if (!Number.isFinite(parsed) || parsed < 1 || parsed > 6) return null;
  return Math.trunc(parsed);
}

function normalizeRecord(item: any): SlaughterOpRecord {
  const status = normalizeStatus(item?.status);

  return {
    ...item,
    animalId: Number(item?.animalId ?? 0),
    status,
    packingZone: status === "packing" ? normalizePackingZone(item?.packingZone) : null,
  };
}

function normalizeRecords(value: unknown): SlaughterOpRecord[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => normalizeRecord(item))
    .filter((item) => Number.isFinite(item.animalId) && item.animalId > 0);
}

const store = createServerStateStore<SlaughterOpRecord[]>(
  "slaughter_ops",
  [],
  normalizeRecords
);

export function getSlaughterOps() {
  return store.getState();
}

export async function rehydrateSlaughterOps() {
  return store.rehydrate();
}

export async function setAllSlaughterOps(
  updater:
    | SlaughterOpRecord[]
    | ((prev: SlaughterOpRecord[]) => SlaughterOpRecord[])
) {
  return store.setState((prev) =>
    normalizeRecords(typeof updater === "function" ? updater(prev) : updater)
  );
}

export function subscribeSlaughterOps(listener: () => void) {
  return store.subscribe(listener);
}

export async function resetSlaughterOpsStore() {
  return store.reset();
}

export async function setSlaughterOpStatus(
  animalId: number,
  status: SlaughterOpStatus,
  extra: Record<string, any> = {}
) {
  return setAllSlaughterOps((prev) => {
    const next = [...prev];
    const index = next.findIndex((item) => item.animalId === animalId);

    if (index >= 0) {
      next[index] = normalizeRecord({
        ...next[index],
        status,
        ...extra,
        animalId,
      });
    } else {
      next.push(
        normalizeRecord({
          animalId,
          status,
          ...extra,
        })
      );
    }

    return next;
  });
}

export async function upsertSlaughterOp(
  recordOrAnimalId: SlaughterOpRecord | number,
  next?: SlaughterOpStatus | Partial<SlaughterOpRecord>
) {
  if (typeof recordOrAnimalId === "number") {
    const animalId = recordOrAnimalId;

    if (typeof next === "string") {
      return setSlaughterOpStatus(animalId, normalizeStatus(next));
    }

    return setAllSlaughterOps((prev) => {
      const patch = next ?? {};
      const index = prev.findIndex((item) => item.animalId === animalId);
      const current =
        index >= 0 ? prev[index] : ({ animalId, status: "queued" } as SlaughterOpRecord);

      const merged = normalizeRecord({
        ...current,
        ...patch,
        animalId,
      });

      if (index >= 0) {
        const copy = [...prev];
        copy[index] = merged;
        return copy;
      }

      return [...prev, merged];
    });
  }

  const record = normalizeRecord(recordOrAnimalId);

  return setAllSlaughterOps((prev) => {
    const index = prev.findIndex((item) => item.animalId === record.animalId);

    if (index >= 0) {
      const copy = [...prev];
      copy[index] = normalizeRecord({
        ...copy[index],
        ...record,
      });
      return copy;
    }

    return [...prev, record];
  });
}

export async function removeSlaughterOp(animalId: number) {
  return setAllSlaughterOps((prev) =>
    prev.filter((item) => item.animalId !== animalId)
  );
}

export async function cleanupSlaughterOps(
  animalsOrIds?: number[] | Array<{ id: number }>
) {
  if (!Array.isArray(animalsOrIds) || animalsOrIds.length === 0) {
    return store.getState();
  }

  const validSet = new Set(
    animalsOrIds
      .map((item) =>
        typeof item === "number" ? item : Number(item?.id ?? 0)
      )
      .filter((id) => Number.isFinite(id) && id > 0)
  );

  return setAllSlaughterOps((prev) =>
    prev.filter((item) => validSet.has(item.animalId))
  );
}

export function deriveSlaughterOpStatus(
  animal: {
    id: number;
    durum?: string;
    tamamlandi?: boolean;
    kesimeUygun?: boolean;
  },
  source?: Map<number, SlaughterOpStatus> | SlaughterOpRecord[]
): SlaughterOpStatus {
  if (source instanceof Map) {
    const found = source.get(animal.id);
    if (found) return found;
  }

  if (Array.isArray(source)) {
    const found = source.find((item) => item.animalId === animal.id);
    if (found?.status) return found.status;
  }

  if (animal.tamamlandi || animal.durum === "Teslim Edildi") {
    return "done";
  }

  if (animal.durum === "Kesildi") {
    return "cut";
  }

  if (animal.kesimeUygun) {
    return "queued";
  }

  return "queued";
}